import { gql, useQuery } from '@apollo/client';
import {
  InfoIcon,
  Modal,
  ModalRoot,
  Button as WebexButton,
} from '@native-instruments/webex-ni';
import { Box, Flex, Text } from '@theme-ui/components';
import isEmpty from 'lodash.isempty';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormattedList, FormattedMessage, useIntl } from 'react-intl';

import {
  Alert,
  AlertAction,
  AlertMessage,
  AlertTitle,
  AlertType,
} from '@app/components/Alert';
import { Button } from '@app/components/Button';
import CheckoutBackLink from '@app/components/CheckoutBackLink';
import { FormattedCosts } from '@app/components/FormattedCosts';
import FormattedCountryName from '@app/components/FormattedCountryName';
import FormattedMoney from '@app/components/FormattedMoney';
import Link from '@app/components/Link';
import { H2 } from '@app/components/typography';
import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';
import { isNotNullish } from '@app/helpers/predicate';
import { pushPerpetualEcommerceEvent } from '@app/helpers/tracking';
import { useDuplicateLicense } from '@app/hooks/useDuplicateLicense';
import { useFireOnce } from '@app/hooks/useFireOnce';
import useGTMTracking from '@app/hooks/useGTMTracking';
import useRouterPush from '@app/hooks/useRouterPush';
import useSimpleCart from '@app/hooks/useSimpleCart';
import {
  type UnqualifiedProducts,
  useUnqualifiedLicense,
} from '@app/hooks/useUnqualifiedLicense';
import { APPLIED_GIFT_CARDS_QUERY } from '@app/pages/Cart/CartGiftCard/CartGiftCard.queries';
import type { AppliedGiftCardsQuery } from '@app/pages/Cart/CartGiftCard/__generated__/AppliedGiftCardsQuery';
import messages from '@app/pages/Checkout/Review/Review.messages';
import {
  AddressCard,
  AddressCardTitle,
  ReviewError,
  ReviewTable,
  ShippingCard,
} from '@app/pages/Checkout/Review/Review.styles';
import TermsAndConditions from '@app/pages/Checkout/Review/TermsAndConditions';
import type { ReviewAddressQuery } from '@app/pages/Checkout/Review/__generated__/ReviewAddressQuery';
import { APP_BASE_URL } from '@config/common.config';
import Cookies from 'js-cookie';
import { useParams } from 'react-router-dom';

const PAYMENT_NI_V2_COOKIE = 'payment_ni_v2';

const Review = () => {
  const { language } = useParams<{
    language: string;
  }>();
  const intl = useIntl();
  const push = useRouterPush();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const paymentV2Enabled = Cookies.get(PAYMENT_NI_V2_COOKIE);

  const [isDuplicatedModal, setOpenDuplicatedModal] = useState(false);
  // Needs to be disabled for now, since has some side effects. See comments of ticket [WWW-12332]
  // const [isUnqualifiedModal, setOpenUnqualifiedModal] = useState(false);

  const { cartId } = useGetCartId();
  const {
    items,
    prices,
    loading: cartLoading,
    storeConfig,
    appliedCoupons,
    selectedShippingMethod,
  } = useSimpleCart();
  const fireEventOnce = useFireOnce();

  const products = items
    ?.map((item) => ({
      name: item?.product.name || '',
      sku: item?.product.sku || '',
      sdbsId: item?.product.sdbsId || '',
    }))
    .filter(({ name, sku }) => !isEmpty(name) || !isEmpty(sku));

  const { hasDuplicatedProducts, duplicatedProductNames } =
    useDuplicateLicense();

  const { hasUnqualifiedLicense, unqualifiedProducts } = useUnqualifiedLicense({
    products,
  });

  const { data, loading: addressLoading } = useQuery<ReviewAddressQuery>(
    REVIEW_ADDRESS_QUERY,
    {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      ssr: false,
    },
  );

  useGTMTracking({ step: 'review', items, prices, storeConfig });

  const [termsError, setTermsError] = useState(false);
  const { discounts, appliedTaxes } = prices ?? {};

  const validDiscounts = useMemo(
    () => discounts?.filter(isNotNullish),
    [discounts],
  );

  const validTaxes = useMemo(
    () => appliedTaxes?.filter(isNotNullish),
    [appliedTaxes],
  );

  useEffect(() => {
    fireEventOnce(() => {
      pushPerpetualEcommerceEvent('review_order', {
        currency: prices?.grandTotal?.currency,
        value: prices?.grandTotal?.value,
        shipping_tier: selectedShippingMethod?.methodCode || '',
        shipping: selectedShippingMethod?.amountInclTax.value,
        coupons: appliedCoupons,
        items: items?.map((item) => ({
          // item_id/item_name should come from the parent product,
          // but it's not available in the cart item data yet.
          // @TODO [ECOM-85, ECOM-84]
          item_id: item?.product?.sku,
          item_name: item?.product?.name,
          sku_id: item?.product?.sku,
          sku_name: item?.product?.name,
          sdbs_id: item?.product?.sdbsId,
          price: item?.prices?.price.value || 0,
          quantity: item?.quantity,
        })),
      });
    });
  }, [appliedCoupons, items, prices, selectedShippingMethod, fireEventOnce]);

  const redirect = useCallback(() => {
    if (paymentV2Enabled) {
      // We need to force a new HTTP request to make sure the HAProxy rule
      // to render the payment page v2 under the 'payment_ni_v2' cookie is triggered.
      window.location.href = `${APP_BASE_URL}/${language}/shop/checkout/payment/`;
    } else {
      push('/:language/shop/checkout/payment/');
    }
  }, [push, language, paymentV2Enabled]);

  const onContinue = useCallback(() => {
    if (!termsAccepted) {
      setTermsError(true);
      return;
    }

    if (hasDuplicatedProducts) {
      setOpenDuplicatedModal(true);
    }
    // else if (hasUnqualifiedLicense) {
    //   setOpenUnqualifiedModal(true);
    // }
    else {
      redirect();
    }
  }, [redirect, termsAccepted, hasDuplicatedProducts]);

  const includingTax = storeConfig?.taxSalesDisplaySubtotal === 'including';

  if (cartLoading || addressLoading) return null;

  const billingAddress = data?.magento2?.customerCart?.billingAddress;
  const isVirtual = data?.magento2?.customerCart?.isVirtual;
  const shippingAddress = data?.magento2?.customerCart?.shippingAddresses[0];
  const shippingMethod = shippingAddress?.selectedShippingMethod;

  const hasDuplicatedMultiProduct = duplicatedProductNames.length > 1;
  // const hasUnqualifiedMultiProduct = unqualifiedProducts.length > 1;

  return (
    <>
      {hasDuplicatedProducts && (
        <>
          <DuplicateLicenseWarning
            duplicatedProductNames={duplicatedProductNames}
          />
          <WarningModal
            isOpen={isDuplicatedModal}
            setIsOpen={setOpenDuplicatedModal}
            title={intl.formatMessage(
              hasDuplicatedMultiProduct
                ? messages.duplicateModalTitleMulti
                : messages.duplicateModalTitleSingle,
            )}
            description={
              hasDuplicatedMultiProduct ? (
                <FormattedMessage
                  {...messages.duplicateModalDescriptionMulti}
                />
              ) : (
                <FormattedMessage
                  {...messages.duplicateModalDescriptionSingle}
                  values={{
                    product: duplicatedProductNames[0],
                  }}
                />
              )
            }
            onContinue={redirect}
          />
        </>
      )}

      {hasUnqualifiedLicense && (
        <>
          <UnqualifiedLicenseWarning
            unqualifiedProducts={unqualifiedProducts}
          />
          {/* Needs to be disabled for now, since has some side effects
             See comments of ticket [WWW-12332]
            <WarningModal
            isOpen={isUnqualifiedModal}
            setIsOpen={setOpenUnqualifiedModal}
            title={intl.formatMessage(
              hasUnqualifiedMultiProduct
                ? messages.wrongLicenseModalTitleMulti
                : messages.wrongLicenseModalTitleSingle,
            )}
            description={
              hasUnqualifiedMultiProduct ? (
                <FormattedMessage {...messages.wrongLicenseModalContentMulti} />
              ) : (
                <FormattedMessage
                  {...messages.wrongLicenseModalContentSingle}
                  values={{
                    product: unqualifiedProducts[0]?.productName ?? '',
                  }}
                />
              )
            }
            onContinue={redirect}
          /> */}
        </>
      )}

      <H2 as="h1">
        <FormattedMessage {...messages.title} />
      </H2>
      <ReviewTable alternateRows={false}>
        <thead>
          <tr>
            <th>
              <FormattedMessage {...messages.itemProductHeader} />
            </th>
            <th>
              <FormattedMessage {...messages.itemQuantityHeader} />
            </th>
            <th>
              <FormattedMessage {...messages.itemPriceHeader} />
            </th>
            <th>
              <FormattedMessage {...messages.itemTotalHeader} />
            </th>
          </tr>
        </thead>
        <tbody>
          {items?.map((item, idx) =>
            item ? (
              <tr key={idx} data-testid={`product-${item.product.sku}-line`}>
                <th
                  scope="row"
                  data-testid={`product-${item.product.sku}-title`}
                >
                  {item.product.name}
                </th>
                <td data-testid={`product-${item.product.sku}-quantity`}>
                  {item.quantity}
                </td>
                <td data-testid={`product-${item.product.sku}-price`}>
                  {includingTax ? (
                    <FormattedMoney {...item.prices?.priceIncludingTax} />
                  ) : (
                    <FormattedMoney {...item.prices?.price} />
                  )}
                </td>
                <td data-testid={`product-${item.product.sku}-total-price`}>
                  {includingTax ? (
                    <FormattedMoney {...item.prices?.rowTotalIncludingTax} />
                  ) : (
                    <FormattedMoney {...item.prices?.rowTotal} />
                  )}
                </td>
              </tr>
            ) : null,
          )}
          <tr data-table="review-subtotal">
            <th scope="row" colSpan={3}>
              <FormattedMessage {...messages.subtotal} />
            </th>
            <td data-testid="subtotal-price">
              {includingTax ? (
                <FormattedMoney {...prices?.subtotalIncludingTax} />
              ) : (
                <FormattedMoney {...prices?.subtotalExcludingTax} />
              )}
            </td>
          </tr>
          {validDiscounts?.map(({ label, amount }) => (
            <tr key={label}>
              <th scope="row" colSpan={3}>
                <FormattedMessage {...messages.discount} />
              </th>
              <td data-testid="discount-amount">
                <FormattedMoney
                  currency={amount.currency}
                  value={(amount.value ?? 0) * -1}
                />
              </td>
            </tr>
          ))}
          {validTaxes?.map(
            ({ label, amount }) =>
              !includingTax && (
                <tr key={label}>
                  <th scope="row" colSpan={3}>
                    <FormattedMessage {...messages.exTax} />
                    {label && ` (${label})`}
                  </th>
                  <td data-testid="tax-amount">
                    <FormattedMoney {...amount} />
                  </td>
                </tr>
              ),
          )}
          <AppliedGiftCards />
          {!isVirtual && shippingMethod && (
            <tr>
              <th scope="row" colSpan={3}>
                <FormattedMessage {...messages.shippingHandling} />
                {` (${shippingMethod?.carrierTitle})`}
              </th>
              <td data-testid="shipping-price">
                <FormattedCosts
                  isShippingCost
                  amount={
                    shippingMethod?.[includingTax ? 'amountInclTax' : 'amount']
                  }
                />
              </td>
            </tr>
          )}
          <tr data-table="review-total">
            <th scope="row" colSpan={3}>
              <FormattedMessage {...messages.grandTotal} />
            </th>
            <td data-testid="total-price">
              <FormattedMoney {...prices?.grandTotal} />
            </td>
          </tr>
        </tbody>
      </ReviewTable>
      <Flex
        sx={{
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
        mt={3}
        mb={3}
      >
        <AddressCard>
          <FormattedMessage {...messages.billingTitle}>
            {(t) => (
              <AddressCardTitle data-testid="billing-card">
                {t}
              </AddressCardTitle>
            )}
          </FormattedMessage>
          <span data-testid="billing-firstname">
            {billingAddress?.firstname}
          </span>{' '}
          <span data-testid="billing-lastname">{billingAddress?.lastname}</span>{' '}
          <br />
          {billingAddress?.company && (
            <>
              <span data-testid="billing-company">
                {billingAddress?.company}
              </span>
              <br />
            </>
          )}
          <span data-testid="billing-addressLine1">
            {billingAddress?.street[0]}
          </span>{' '}
          <br />
          <span data-testid="billing-postcode">{billingAddress?.postcode}</span>{' '}
          <span data-testid="billing-city">{billingAddress?.city}</span> <br />
          <span data-testid="billing-country">
            <FormattedCountryName code={billingAddress?.country.code} />
          </span>
        </AddressCard>
        {!isVirtual && (
          <>
            <AddressCard>
              <FormattedMessage {...messages.shippingTitle}>
                {(t) => (
                  <AddressCardTitle data-testid="shipping-card">
                    {t}
                  </AddressCardTitle>
                )}
              </FormattedMessage>
              <span data-testid="shipping-firstname">
                {shippingAddress?.firstname}
              </span>{' '}
              <span data-testid="shipping-lastname">
                {shippingAddress?.lastname}
              </span>{' '}
              <br />
              {shippingAddress?.company && (
                <>
                  <span data-testid="shipping-company">
                    {shippingAddress?.company}
                  </span>
                  <br />
                </>
              )}
              <span data-testid="shipping-addressLine1">
                {shippingAddress?.street[0]}
              </span>{' '}
              <br />
              <span data-testid="shipping-postcode">
                {shippingAddress?.postcode}
              </span>{' '}
              <span data-testid="shipping-city">{shippingAddress?.city}</span>{' '}
              <br />
              <span data-testid="shipping-country">
                <FormattedCountryName code={shippingAddress?.country.code} />
              </span>
            </AddressCard>
            <ShippingCard>
              <FormattedMessage {...messages.shippingMethod}>
                {(t) => (
                  <AddressCardTitle data-testid="shipping-method-card">
                    {t}
                  </AddressCardTitle>
                )}
              </FormattedMessage>

              <span data-testid="shipping-method-title">
                {shippingMethod?.carrierTitle}
              </span>
            </ShippingCard>
          </>
        )}
      </Flex>
      {termsError && (
        <ReviewError data-testid="terms-error">
          <FormattedMessage {...messages.termsError} />
        </ReviewError>
      )}
      <TermsAndConditions
        setTermsAccepted={setTermsAccepted}
        error={termsError}
        cartId={cartId}
      />
      <Flex
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        pt={3}
      >
        {isVirtual ? (
          <CheckoutBackLink to="/:language/shop/checkout/address">
            <FormattedMessage
              {...messages[isVirtual ? 'backAddress' : 'backShipping']}
            />
          </CheckoutBackLink>
        ) : (
          <CheckoutBackLink to="/:language/shop/checkout/delivery">
            <FormattedMessage
              {...messages[isVirtual ? 'backAddress' : 'backShipping']}
            />
          </CheckoutBackLink>
        )}

        <Button
          data-testid="review-continue-button"
          type="primary"
          onClick={onContinue}
        >
          <FormattedMessage {...messages.continue} />
        </Button>
      </Flex>
    </>
  );
};

export default Review;

const AppliedGiftCards = () => {
  const { cartId } = useGetCartId();
  const { data, loading } = useQuery<AppliedGiftCardsQuery>(
    APPLIED_GIFT_CARDS_QUERY,
    {
      variables: { cartId },
      skip: !cartId,
    },
  );
  const appliedGiftCards = data?.magento2?.cart?.appliedGiftCards;

  const validAppliedGiftCards = useMemo(
    () =>
      appliedGiftCards
        ?.filter(isNotNullish)
        .filter((el) => Boolean(el.appliedBalance?.value)),
    [appliedGiftCards],
  );

  if (loading) return null;

  return (
    <>
      {validAppliedGiftCards?.map(({ appliedBalance, code }) => (
        <tr key={code}>
          <th scope="row" colSpan={3}>
            <FormattedMessage {...messages.giftCard} /> ({code})
          </th>
          <td>
            <FormattedMoney
              currency={appliedBalance?.currency}
              value={(appliedBalance?.value ?? 0) * -1}
            />
          </td>
        </tr>
      ))}
    </>
  );
};

const WarningModal = ({
  title,
  isOpen,
  setIsOpen,
  description,
  onContinue,
}: {
  title: ReactNode;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  description: ReactNode;
  onContinue: () => void;
}) => {
  return (
    <div
      className="ni-tailwind-namespace"
      data-testid="duplicate-license-modal"
    >
      <ModalRoot isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
          <div className="text-center rounded p-4">
            <div className="flex justify-center mb-2">
              <InfoIcon className="!text-grey-90 h-6 w-6" />
            </div>
            <h1 className="webex-h3">{title}</h1>
            <p className="mt-2 text-md">{description}</p>
            <FormattedMessage {...messages.areYouSure} />

            <div className="mt-4 flex justify-center">
              <div className="mr-4 font-medium flex justify-center items-center">
                <Link
                  onClick={() => setIsOpen(false)}
                  to="/:language/shop/cart"
                  data-testid="duplicate-license-returnToCart-button"
                >
                  <span data-testid="return-to-cart">
                    <FormattedMessage {...messages.returnToCart} />
                  </span>
                </Link>
              </div>
              <WebexButton
                data-testid="review-continue-button"
                variant="primary"
                onClick={() => {
                  setIsOpen(false);
                  onContinue();
                }}
              >
                <FormattedMessage {...messages.continue} />
              </WebexButton>
            </div>
          </div>
        </Modal>
      </ModalRoot>
    </div>
  );
};

const DuplicateLicenseWarning = ({
  duplicatedProductNames,
}: {
  duplicatedProductNames: (string | null | undefined)[];
}) => {
  return (
    <Box mb={20}>
      <Alert
        type={AlertType.Warning}
        data-testid="duplicate-license-warning"
        actions={
          <AlertAction>
            <Link to="/:language/shop/cart">
              <span data-testid="return-to-cart">
                <FormattedMessage {...messages.returnToCart} />
              </span>
            </Link>
          </AlertAction>
        }
      >
        <AlertTitle>
          <FormattedMessage
            {...messages.content}
            values={{
              product: (
                <FormattedList
                  type="conjunction"
                  value={duplicatedProductNames}
                />
              ),
            }}
          />
        </AlertTitle>
      </Alert>
    </Box>
  );
};

const UnqualifiedLicenseWarning = ({
  unqualifiedProducts,
}: {
  unqualifiedProducts: UnqualifiedProducts;
}) => {
  return (
    <>
      {unqualifiedProducts.map((unqualifiedProduct) => {
        return (
          <Box mb={20} key={unqualifiedProduct?.localizedMessage?.id}>
            <Alert
              type={AlertType.Warning}
              data-testid="unqualified-license-warning"
              actions={
                <AlertAction css={{ alignSelf: 'start' }}>
                  <Link to="/:language/shop/cart">
                    <FormattedMessage {...messages.returnToCart} />
                  </Link>
                </AlertAction>
              }
            >
              <AlertTitle
                data-testid={`title-${unqualifiedProduct?.productSku}`}
              >
                {unqualifiedProduct?.productName ?? ''}
              </AlertTitle>
              <AlertMessage>
                <Text
                  sx={{ fontWeight: '600', display: 'block' }}
                  mb={1}
                  dangerouslySetInnerHTML={{
                    __html:
                      unqualifiedProduct?.requiredBaseProductsTranslation ?? '',
                  }}
                />
                <FormattedMessage {...messages.ownershipWarning} />
              </AlertMessage>
            </Alert>
          </Box>
        );
      })}
    </>
  );
};

export const REVIEW_ADDRESS_QUERY = gql`
  query ReviewAddressQuery {
    magento2 {
      customerCart {
        id
        isVirtual
        shippingAddresses {
          firstname
          lastname
          street
          postcode
          city
          company
          country {
            code
          }
          selectedShippingMethod {
            carrierTitle
            amount {
              value
              currency
            }
            amountInclTax {
              value
              currency
            }
          }
        }
        billingAddress {
          firstname
          lastname
          street
          postcode
          city
          company
          country {
            code
          }
        }
      }
    }
  }
`;
